import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasLockerUseCase } from '../../common/lockerUseCases';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Box } from '@mui/material';
import {
    AssetAvailabilityChart,
    AssetOverviewSummary,
} from '../dashboard/AssetAvailabilityChart';
import PageHeadline from '../../components/PageHeadline';
import PageSubtitle from '../../components/PageSubtitle';
import SearchBar from '../../components/SearchBar';
import CustomButton from '../../components/CustomButton';
import DropdownMenu, { DropdownMenuItem } from '../../components/DropdownMenu';
import { makeHasPermissionSelector, tenantSelector } from '../../selector/authenticationSelector';
import Permission from '../../common/permissions';
import { LEGACY_TENANT_SLUGS } from '../../api/types';
import { paths } from '../../routes';
import { RootState } from '../../store/store';
import { getCreateBookingOptionsForTenant } from '../../config/bookingServiceMapping';
import SegmentedTabs from '../../components/SegmentedTabs';
import { ROOM_PRODUCT_USE_CASES } from '../services/serviceConfigurationRules';
import { roomUseCasesOf } from '../../utils/roomUseCases';
import RoomWeekCalendar from './week/RoomWeekCalendar';
import RetiredObjectsList from './RetiredObjectsList';

type OverviewView = 'month' | 'week' | 'retired';
const VIEW_PARAM = 'view';

/** The view a URL asks for; the week view only where the tenant has one. */
const overviewViewFrom = (requested: string | null, hasWeekCalendar: boolean): OverviewView => {
    if (requested === 'retired') return 'retired';
    if (requested === 'week' && hasWeekCalendar) return 'week';
    return 'month';
};

const AssetsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    // Reported by the chart, which is where the grouping and the filters live.
    const [summary, setSummary] = useState<AssetOverviewSummary | null>(null);
    const selectedTenant = useSelector(tenantSelector);
    const hasCreateBookingsPermission = useSelector(makeHasPermissionSelector(Permission.createBookings()));
    const hasCreateAssetsPermission = useSelector(makeHasPermissionSelector(Permission.createAssets()));
    const config = useSelector((state: RootState) => state.authentication.config);
    // Narrow the asset filter to the tenant's actual use cases. Self-service
    // tenants use their `config.services` (the source of truth — synthesized
    // from the tenant's stored services); demo keeps the legacy venue+apartment
    // pair. Legacy tenants are unrestricted.
    const isSelfServiceTenant = !!selectedTenant && !LEGACY_TENANT_SLUGS.has(selectedTenant);
    const allowedUseCases: string[] | undefined = isSelfServiceTenant
        ? config?.services
        : selectedTenant === 'demo'
            ? ['apartment', 'venue']
            : undefined;

    // The week calendar (#3039) shows objects whose group IS the object —
    // offered when the tenant has any, whichever use case they come from.
    const hasWeekCalendar = (config?.services ?? []).some(
        (service: string) => ROOM_PRODUCT_USE_CASES.has(service.toLowerCase()),
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedView = searchParams.get(VIEW_PARAM);
    // "Ausgemustert" (PO, 2026-10-15): the retired objects, which every other
    // view leaves out. Offered to every tenant, rooms or stations.
    const view = overviewViewFrom(requestedView, hasWeekCalendar);
    const changeView = (next: OverviewView) => {
        // The views keep different parameters; none should inherit another's.
        setSearchParams(next === 'month' ? {} : { [VIEW_PARAM]: next });
    };
    const viewTabs = [
        { key: 'month' as const, label: t('dashboard.assets_availability.view.month') },
        ...(hasWeekCalendar ? [{ key: 'week' as const, label: t('dashboard.assets_availability.view.week') }] : []),
        { key: 'retired' as const, label: t('dashboard.assets_availability.view.retired') },
    ];

    const createMenuItems: DropdownMenuItem[] = useMemo(() => {
        if (!config?.services) return [];

        const bookingOptions = getCreateBookingOptionsForTenant(
            config.services,
            config.createBookingOptions,
        );

        // Each entry on its own feature's string (PO, 2026-10-07): bookings on
        // `create:bookings`, object / house / station creation on
        // `create:assets`. The menu used to sit behind `create:bookings` as a
        // whole, so the asset entries borrowed the booking permission.
        const items: DropdownMenuItem[] = hasCreateBookingsPermission
            ? bookingOptions.map(option => ({
                id: option.id,
                label: t(option.translationKey),
                onClick: () => navigate(option.route),
            }))
            : [];

        const hasRoomFamily = roomUseCasesOf(config.services).length > 0;
        if (hasRoomFamily && hasCreateBookingsPermission) {
            if (items.length > 0) {
                items[items.length - 1] = { ...items[items.length - 1], divider: true };
            }
            items.push({
                id: 'series-booking',
                label: t('bookings.create.series_booking.title'),
                onClick: () => navigate(paths.createSeriesBooking),
            });
            items.push({
                id: 'blocker',
                label: t('bookings.create.blocker.title'),
                onClick: () => navigate(paths.createBlocker),
                divider: hasCreateAssetsPermission,
            });
        }
        if (hasRoomFamily && hasCreateAssetsPermission) {
            items.push({
                id: 'create-asset',
                label: t('asset_create.menu_label'),
                onClick: () => navigate(paths.createAsset),
            });
            // A house holds rooms (plan room-houses-and-combining-rooms §4
            // row 1). It is the station wizard's variant, but it belongs to
            // the room tenants — the opposite set from "Station anlegen".
            items.push({
                id: 'create-house',
                label: t('asset_create.house_menu_label'),
                onClick: () => navigate(paths.createHouse),
            });
        }

        // Station & object creation (#2134). Objects are managed here, and a
        // station is how a set of them gets created — so it belongs in this
        // menu rather than in a sidebar entry of its own (PO, 2026-08-28).
        //
        // Its own condition, not the apartment/venue block above: the wizard
        // serves station tenants, which is the opposite set from the one that
        // gets "Objektanlage". Gated on the tenant actually having
        // locker-backed use cases — for a room-only tenant the wizard's
        // product step is empty by construction, so the entry used to show
        // and dead-end (innodom review 1.1/4.4).
        if (hasCreateAssetsPermission && hasLockerUseCase(config.services)) {
            // A divider only when this opens the creation group. With
            // apartment/venue the group is already open and "Objektanlage"
            // sits directly above.
            if (!hasRoomFamily && items.length > 0) {
                items[items.length - 1] = { ...items[items.length - 1], divider: true };
            }
            items.push({
                id: 'create-station',
                label: t('asset_create.station_menu_label'),
                onClick: () => navigate(paths.createStation),
            });
        }

        return items;
    }, [config, t, navigate, hasCreateBookingsPermission, hasCreateAssetsPermission]);

    return (
        <div className={'p-6 flex flex-col gap-2'}>
            {/* Page Header */}
            <div className={'flex flex-row gap-2 justify-between items-center mb-5'}>
                <div className={'flex flex-col'}>
                    <PageHeadline>
                        {t('dashboard.assets_availability.title')}
                    </PageHeadline>
                    {/* Counts what the month chart groups; the week view
                        lists rooms only, so the counts would not describe it. */}
                    {summary && view === 'month' && (
                        <PageSubtitle>
                            {t('dashboard.assets_availability.count_stations', {
                                count: summary.stations,
                            })}
                            {' · '}
                            {t('dashboard.assets_availability.count_objects', {
                                count: summary.objects,
                            })}
                        </PageSubtitle>
                    )}
                </div>
                <div className={'flex flex-row items-center gap-2'}>
                    <SearchBar
                        className={'hidden sm:block'}
                        placeholder={t('dashboard.assets_availability.search_placeholder')}
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />
                    {createMenuItems.length > 0 && (
                        <DropdownMenu
                            trigger={
                                <CustomButton
                                    title={t('bookings.bookings_table.create_button')}
                                    isFilled={true}
                                    onClick={() => {}}
                                />
                            }
                            items={createMenuItems}
                        />
                    )}
                </div>
            </div>
            <Box>
                <SearchBar
                    className={'w-full sm:hidden mb-5'}
                    placeholder={t('dashboard.assets_availability.search_placeholder')}
                    value={searchQuery}
                    onChange={setSearchQuery}
                />
            </Box>

            {/* Chart.

                `min-w-0` is load-bearing, not tidiness: this is a flex item of
                the `flex-col` above, and a flex item's default `min-width:auto`
                refuses to shrink below its content. Without it the card is as
                wide as its widest child, the PAGE scrolls sideways instead of
                the table's own scroll container, and the right-hand columns sit
                off-screen with no way to reach them (QA #2439, defect G). */}
            <SegmentedTabs<OverviewView>
                className={'mb-3'}
                items={viewTabs}
                value={view}
                onChange={changeView}
            />
            <div className={'min-w-0 p-4 bg-text-contrast rounded-[5px] shadow-sm'}>
                {view === 'retired' && <RetiredObjectsList searchQuery={searchQuery} />}
                {view === 'week' && (
                    <RoomWeekCalendar
                        searchQuery={searchQuery}
                        allowedUseCases={allowedUseCases}
                    />
                )}
                {view === 'month' && (
                    <AssetAvailabilityChart
                        searchQuery={searchQuery}
                        allowedUseCases={allowedUseCases}
                        onSummaryChange={setSummary}
                    />
                )}
            </div>
        </div>
    );
};

export default AssetsPage;
